import type { EligibilityReason, ExtensionEligibility } from '../../services/extendContext.js';

/** The subset of `ExtensionEligibility.limits` the two policy sentences below interpolate. */
type PolicyLimits = ExtensionEligibility['limits'];

/**
 * 131 (T041): a minute count in the platform's own operator-facing unit, restated in whichever of
 * minutes/hours/days reads most naturally — an operator enters "10080" for a 7-day notice period,
 * and no customer should have to do that arithmetic themselves to understand a refusal.
 *
 * Whole units only (no "1.5 days"): every value that reaches here is either an operator-entered
 * setting (bounded to whole minutes by `platform-config/routes.ts`'s zod schema) or a remainder
 * derived from one, so an exact whole-unit match is common and a fraction would only ever be the
 * odd remainder — which reads fine as minutes.
 */
function formatMinutes(minutes: number): string {
  if (minutes % 1440 === 0 && minutes >= 1440) {
    const days = minutes / 1440;
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  if (minutes % 60 === 0 && minutes >= 60) {
    const hours = minutes / 60;
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

/**
 * 131 (FR-010, research D10): every sentence the extend screen says about *why* a rental cannot be
 * extended, and where to go about it.
 *
 * Deliberately NOT in `components/ui/resultWording.ts`. That module is scoped to **read outcomes** —
 * session ended, gone, refused, payment not confirmed — and its docstring is explicit that a screen
 * supplies only the domain noun and never a sentence. These are **domain policy** statements
 * ("this rental has already been extended"), which is a different thing. Putting them there would
 * blur a boundary feature 064 drew on purpose.
 *
 * The split this file implements is the app's existing habit, not a new invention: the server emits
 * a machine-readable code, the app owns every word the customer reads. `LoginPage.tsx` already
 * branches on `err.code === 'EMAIL_NOT_VERIFIED'` this way, and `services/api.ts` carries `code`
 * through into `ApiError`.
 *
 * Two rules the wording follows, both from the spec rather than taste:
 *
 *  - **Never describe the car as unavailable when it is not.** An exhausted allowance, a notice
 *    period and an already-in-flight extension are all reasons the *rental* cannot move, and the car
 *    may be sitting free. Conflating them tells the customer something false.
 *  - **Say what to do next, when there is something to do.** A verification gate has a remedy and a
 *    place to go; `no_window` does not, and pretending otherwise sends people to a dead end.
 */

export interface ReasonCopy {
  /** The sentence the screen states. */
  message: string;
  /** An onward action, only where one genuinely exists. */
  action?: { label: string; href: string };
}

const REASONS: Record<EligibilityReason, ReasonCopy> = {
  booking_not_extendable: {
    // Deliberately does not name the status. "completed" and "cancelled" mean nothing to a
    // customer, and the one thing they need to know is that this screen cannot help them.
    message: 'This booking can no longer be extended.',
  },
  overdue: {
    // The return time has passed, so support has to be involved — an extension would be measured
    // from a moment already gone. Naming the remedy matters because the customer still has the car.
    message:
      'This rental’s return time has already passed, so it can’t be extended here. Please contact support.',
  },
  email_not_verified: {
    message: 'Please verify your email address before extending a booking.',
    action: { label: 'Verify email', href: '/account/verify-email' },
  },
  identity_not_verified: {
    // Named separately from the email gate because the remedy is a different screen. A single
    // generic "your account needs verification" would leave half these customers guessing.
    message: 'Your identity check needs to be approved before you can extend a booking.',
    action: { label: 'Go to identity check', href: '/account/identity' },
  },
  unpaid_extension_exists: {
    // Not "unavailable" — the customer's own unfinished payment is holding the window, and it is
    // recoverable. The history list below already shows it, so the sentence points there.
    message:
      'You already have an extension waiting for payment. Finish or cancel it before starting another.',
  },
  inside_notice_period: {
    // The car may well be free; this is a policy cut-off. Saying "not available" would be a lie.
    message: 'It’s too close to your return time to extend online. Please contact support.',
  },
  allowance_spent: {
    message:
      'This rental has already been extended as far as it can be. Please contact support if you need more time.',
  },
  no_window: {
    // The one reason that IS about the car. No onward action exists — a later booking holds it, and
    // nothing the customer does changes that.
    message: 'This car isn’t free after your current return time, so it can’t be extended.',
  },
};

/**
 * 131 (T041): `reasonCopy` takes the read's own `limits` and states the actual number that
 * blocked this rental, rather than the reason alone — "too close" and "already extended as far as
 * it can be" are true of every refusal of that kind and explain nothing about THIS one.
 *
 * `limits` is optional (and every existing call site omits it) so every other reason keeps its
 * static sentence unchanged — only the two policy reasons this feature introduced read it.
 */
export function reasonCopy(reason: EligibilityReason, limits?: PolicyLimits): ReasonCopy {
  if (reason === 'inside_notice_period' && limits) {
    return {
      message: `It’s too close to your return time to extend online — extensions must be requested at least ${formatMinutes(limits.min_notice_minutes)} before the return time. Please contact support.`,
    };
  }
  if (reason === 'allowance_spent' && limits && limits.max_total_extension_minutes !== null) {
    return {
      message: `This rental has already been extended by its maximum of ${formatMinutes(limits.max_total_extension_minutes)}. Please contact support if you need more time.`,
    };
  }
  return REASONS[reason] ?? { message: 'This booking can’t be extended right now.' };
}

/**
 * Refusal codes the server may answer with when a date HAS been named — the price preview and the
 * reserve. Separate from the reasons above because these arrive mid-flow, after the customer has
 * chosen something, and the wording has to acknowledge that.
 */
const CODES: Record<string, string> = {
  CAR_UNAVAILABLE:
    'This car already has another booking or hold starting before your new return time, so this extension isn’t available. Please choose an earlier return time, or contact support if you need more time.',
  EXTENSION_TOO_LATE: 'It’s too close to your return time to extend online.',
  EXTENSION_LIMIT_EXCEEDED: 'That’s longer than this rental can be extended.',
  EXTENSION_BOOKING_OVERDUE: 'This rental’s return time has already passed.',
  EXTENSION_EXISTS: 'You already have an extension waiting for payment.',
  // 169: the index refused a repeat the code allows — a database whose migration 131 has not run
  // still forbids a second extension after a PAID one. Distinct from EXTENSION_EXISTS on purpose:
  // there is nothing waiting for payment here, so telling the customer to pay would be a lie, and
  // retrying cannot succeed either — hence support rather than "try again".
  EXTENSION_CONFLICT: 'This booking can’t be extended again just now. Please contact support.',
  EXTENSION_NOT_PRICEABLE: 'We can’t price that return time for this car.',
  EMAIL_NOT_VERIFIED: 'Please verify your email address before extending a booking.',
  IDENTITY_NOT_VERIFIED:
    'Your identity check needs to be approved before you can extend a booking.',
  PAYMENT_PROVIDER_ERROR: 'We couldn’t start your payment just now. Please try again in a moment.',
};

/**
 * The sentence for a refusal code. Falls back to a customer-safe sentence rather than to the
 * server's own message — FR-010 forbids internal wording reaching the screen, and today the raw
 * backend string is exactly what this screen renders.
 *
 * 131 (T041): `limits` names the actual number for the two policy codes, the same as `reasonCopy`
 * does for the read-only reasons above — the AppError the server sent already names it (T038's
 * `assertPolicyAllowsExtension`), but FR-010 forbids that raw string reaching the screen, so the
 * number is re-stated from the eligibility read instead of the error text.
 */
export function refusalMessage(code: string | undefined, limits?: PolicyLimits): string {
  if (code === 'EXTENSION_TOO_LATE' && limits) {
    return `It’s too close to your return time to extend online — extensions must be requested at least ${formatMinutes(limits.min_notice_minutes)} before the return time.`;
  }
  if (code === 'EXTENSION_LIMIT_EXCEEDED' && limits && limits.remaining_minutes !== null) {
    return `That’s longer than this rental can still be extended — ${formatMinutes(limits.remaining_minutes)} remain${limits.remaining_minutes === 1 ? 's' : ''} of the maximum.`;
  }
  return (code && CODES[code]) ?? 'We couldn’t extend this booking. Please try again.';
}

/**
 * The failed-limits-read wording (FR-011) is deliberately NOT a sentence in this file.
 *
 * `.claude/rules/frontend.md` is explicit: every user-facing sentence for an absent or failed read
 * comes from `components/ui/resultWording.ts`, and a screen supplies the domain noun, never a
 * sentence. A failed eligibility read is exactly such a case, so the screen calls
 * `lookupFailedMessage(LIMITS_SUBJECT, LIMITS_UNAVAILABLE_HINT)` and the sentence is built there.
 *
 * What belongs here is only the two domain-specific fragments: the noun, and the hint about what
 * the customer can still do — which is guidance, not a read outcome, and is what stops the screen
 * from presenting an unbounded chooser as though it were the real limit.
 */
export const LIMITS_SUBJECT = 'The dates available for this booking';

export const LIMITS_UNAVAILABLE_HINT =
  'You can still try a return time — we’ll check it before taking any payment.';
